import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  DocumentTextIcon,
  DocumentIcon,
  ArrowUpTrayIcon,
  DocumentPlusIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  BellIcon,
  ChevronDownIcon,
  PlusIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { FadeIn, SlideIn, HoverScale } from '../components/animations';
import { motion } from 'framer-motion';
import { getThemeStyles, commonStyles } from '../styles/theme';
import { isFeatureEnabled } from '../config/environment';
import Card from '../components/ui/Card';
import Stat from '../components/ui/Stat';
import NotificationBadge from '../components/ui/NotificationBadge';
import SegmentBar from '../components/ui/SegmentBar';
import { ServiceFactory } from '../services/factory/ServiceFactory';
import { isDemoMode } from '../utils/demo';
import { PipelineMetrics, SoldCasesData, EnrollmentsData } from '../services/interfaces/IApiService';

// Quote interface (simplified from QuotesList)
interface Quote {
  submissionId: string;
  companyName: string;
  transperraRep: string;
  ichraEffectiveDate: string;
  pepm: string;
  status: string;
  brokerName: string;
  employerName: string;
  brokerId: string;
  employerId: string;
  s3Key: string;
  createdAt?: string;
  documents?: any[];
}

// Status Badge Component
const StatusBadge: React.FC<{ status: string; theme: any }> = ({ status, theme }) => {
  const getStatusStyles = () => {
    // Add null check for status
    const statusLower = status?.toLowerCase() || '';
    
    switch (statusLower) {
      case 'active':
      case 'approved':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'pending':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300';
      case 'inactive':
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
    }
  };

  return (
    <span className={`inline-flex px-2.5 py-1 text-sm font-medium rounded-full ${getStatusStyles()}`}>
      {status || 'Unknown'}
    </span>
  );
};

const Dashboard: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const theme = getThemeStyles(isDarkMode);
  const [dashboardData, setDashboardData] = useState({
    totalQuotes: 0,
    activeQuotes: 0,
    totalDocuments: 0,
    recentUploads: 0,
    recentQuotes: [] as Quote[],
    completionRate: 0,
    avgQuoteValue: 0,
    statusCounts: {} as {[key: string]: number},
    thisWeeksQuotes: 0
  });
  const [pipelineMetrics, setPipelineMetrics] = useState<PipelineMetrics | null>(null);
  const [soldCases, setSoldCases] = useState<SoldCasesData | null>(null);
  const [enrollments, setEnrollments] = useState<EnrollmentsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getIdToken, user } = useAuth();

  // Check if we're in demo mode
  const demoMode = isDemoMode();
  // Check if enhanced dashboard is enabled
  const showEnhancedDashboard = demoMode && isFeatureEnabled('enhancedDashboard');

  // Function to filter quotes based on user role and permissions
  const filterQuotesByRole = (quotes: Quote[]) => {
    if (!user) return [];
    
    console.log(`Dashboard: Filtering quotes for user role: ${user.role}, username: ${user.username}`);
    
    switch (user.role) {
      case 'admin':
      case 'kyndly_staff':
        // Admins and Kyndly staff can see all quotes
        console.log(`Dashboard: Admin/Kyndly staff - showing all ${quotes.length} quotes`);
        return quotes;
        
      case 'tpa_admin':
      case 'tpa_user':
        // TPA users can see all quotes within their TPA
        const tpaQuotes = quotes.filter(quote => {
          // Note: We'd need the quote to include tpaId for proper filtering
          // For now, this assumes the API already filters by TPA
          return true;
        });
        console.log(`Dashboard: TPA user - showing ${tpaQuotes.length} quotes within TPA`);
        return tpaQuotes;
        
      case 'broker':
        // Brokers can only see quotes where they are the broker
        const brokerQuotes = quotes.filter(quote => {
          const isMyQuote = quote.brokerId === user.username || quote.brokerId === user.email;
          if (isMyQuote) {
            console.log(`Dashboard: Found broker quote - ${quote.submissionId} for company ${quote.companyName}`);
          }
          return isMyQuote;
        });
        console.log(`Dashboard: Broker - showing ${brokerQuotes.length} out of ${quotes.length} total quotes`);
        return brokerQuotes;
        
      case 'employer':
        // Employers can only see quotes where they are the employer
        const employerQuotes = quotes.filter(quote => {
          const isMyQuote = quote.employerId === user.username || quote.employerId === user.email;
          if (isMyQuote) {
            console.log(`Dashboard: Found employer quote - ${quote.submissionId} for company ${quote.companyName}`);
          }
          return isMyQuote;
        });
        console.log(`Dashboard: Employer - showing ${employerQuotes.length} out of ${quotes.length} total quotes`);
        return employerQuotes;
        
      default:
        console.warn(`Dashboard: Unknown user role: ${user.role}, showing no quotes`);
        return [];
    }
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        const apiService = ServiceFactory.createApiService();

        // If enhanced dashboard is enabled, fetch pipeline metrics
        if (showEnhancedDashboard) {
          console.log('Dashboard: Fetching enhanced dashboard data');
          const [pipeline, sold, enroll] = await Promise.all([
            apiService.getDashboardPipeline(),
            apiService.getSoldCases(),
            apiService.getEnrollments()
          ]);
          setPipelineMetrics(pipeline);
          setSoldCases(sold);
          setEnrollments(enroll);
          setError(null);
        } else {
          // Fetch quotes for simple dashboard
          console.log('Dashboard: Fetching simple dashboard data');
          const quotesResponse = await apiService.Quote.getQuotes();
          const allQuotes = Array.isArray(quotesResponse) ? quotesResponse : (quotesResponse?.quotes || []);

          // Apply role-based filtering
          const filteredQuotes = filterQuotesByRole(allQuotes);

          // Calculate KPIs
          const totalQuotes = filteredQuotes.length;
          const activeQuotes = filteredQuotes.filter((quote: Quote) =>
            quote.status?.toLowerCase() === 'active' ||
            quote.status?.toLowerCase() === 'approved'
          ).length;

          // Calculate closed ratio (last 90 days)
          const ninetyDaysAgo = new Date();
          ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

          const recentQuotes = filteredQuotes.filter((quote: Quote) => {
            const quoteDate = quote.createdAt ? new Date(quote.createdAt) : null;
            return quoteDate && quoteDate >= ninetyDaysAgo;
          });

          const recentClosedQuotes = recentQuotes.filter((quote: Quote) =>
            quote.status?.toLowerCase() === 'completed' ||
            quote.status?.toLowerCase() === 'sold'
          );

          const closedRatio = recentQuotes.length > 0 ? Math.round((recentClosedQuotes.length / recentQuotes.length) * 100) : 0;

          // Calculate average quote value (PEPM)
          const validPepmValues = filteredQuotes
            .map((quote: Quote) => parseFloat(quote.pepm))
            .filter((pepm: number) => !isNaN(pepm));

          const avgQuoteValue = validPepmValues.length > 0
            ? Math.round(validPepmValues.reduce((sum: number, val: number) => sum + val, 0) / validPepmValues.length)
            : 0;

          // Count quotes by status
          const statusCounts: { [key: string]: number } = {};
          filteredQuotes.forEach((quote: Quote) => {
            let status = quote.status?.toLowerCase() || 'unknown';
            if (status === 'unknown') {
              status = 'pending';
            }
            statusCounts[status] = (statusCounts[status] || 0) + 1;
          });

          // Calculate this week's quotes
          const oneWeekAgo = new Date();
          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

          const thisWeeksQuotes = filteredQuotes.filter((quote: Quote) => {
            const quoteSubmissionDate = quote.createdAt ? new Date(quote.createdAt) : null;
            return quoteSubmissionDate && quoteSubmissionDate >= oneWeekAgo;
          }).length;

          // Format recent quotes
          const recentQuotesFormatted = filteredQuotes.slice(0, 5).map((quote: Quote) => ({
            ...quote,
            id: quote.submissionId,
            name: quote.companyName,
            date: quote.createdAt || new Date().toLocaleDateString()
          }));

          setDashboardData({
            totalQuotes,
            activeQuotes,
            totalDocuments: filteredQuotes.length > 0 ? Math.floor(filteredQuotes.length * 2.5) : 0,
            recentUploads: filteredQuotes.length > 0 ? Math.floor(filteredQuotes.length * 0.7) : 0,
            recentQuotes: recentQuotesFormatted,
            completionRate: closedRatio,
            avgQuoteValue,
            statusCounts,
            thisWeeksQuotes
          });

          setError(null);
        }
      } catch (err) {
        console.error("Dashboard: Error fetching dashboard data:", err);
        setError("Unable to load dashboard data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [showEnhancedDashboard, user]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-teal-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <FadeIn>
        <div className="bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-2xl p-6 text-slate-700 shadow-lg">
          <div className="flex items-center space-x-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-teal-500" />
            <h3 className="text-lg font-medium text-slate-900">Dashboard Setup in Progress</h3>
          </div>
          <div className="mt-4 space-y-3">
            <p className="text-slate-600">{error}</p>
            <p className="text-sm text-slate-500">Your dashboard will display key metrics once the data integration is complete.</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 text-sm font-medium px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-500 transition-colors duration-200"
            >
              Refresh Dashboard
            </button>
          </div>
        </div>
      </FadeIn>
    );
  }

  // Enhanced Pipeline Dashboard (Demo Mode + Feature Flag Enabled)
  if (showEnhancedDashboard && pipelineMetrics && soldCases && enrollments) {
    return (
      <div className="min-h-screen bg-white">
        <div className="bg-brand-gradient rounded-brand p-6 mb-6 text-white shadow-brand border border-[#46BC97]/40">
          <h1 className="text-3xl font-bold">Dashboard</h1>
        </div>

        <div className="p-6">
          {/* Pipeline Section Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Pipeline</h2>
            <p className="mt-1 text-sm text-gray-600">Overview of opportunities</p>
          </div>

          {/* Pipeline Cards Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Active Quotes (Total) Card */}
            <div className="overflow-hidden bg-white shadow-sm sm:rounded-lg border border-gray-200">
              <div className="px-4 py-3 sm:px-6">
                <h3 className="text-base/7 font-semibold text-gray-900">Active Quotes (Total)</h3>
              </div>
              <div className="border-t border-gray-100">
                <dl className="divide-y divide-gray-100">
                  <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Count</dt>
                    <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 font-semibold text-right">{pipelineMetrics.activeQuotesTotal.count}</dd>
                  </div>
                  <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Potential Membership</dt>
                    <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 font-semibold text-right">{pipelineMetrics.activeQuotesTotal.membership}</dd>
                  </div>
                  <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Percent ALEs</dt>
                    <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 font-semibold text-right">{pipelineMetrics.activeQuotesTotal.percentALES}</dd>
                  </div>
                  <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Est. Revenue Opportunity</dt>
                    <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 font-semibold text-right">{pipelineMetrics.activeQuotesTotal.revenue}</dd>
                  </div>
                </dl>
                <div className="px-4 py-2 bg-gray-50">
                  <p className="text-xs text-gray-500">*Active Quotes are those more than 45 days out from Effective Date</p>
                </div>
              </div>
            </div>

            {/* Close Ratios Card */}
            <div className="overflow-hidden bg-white shadow-sm sm:rounded-lg border border-gray-200">
              <div className="px-4 py-3 sm:px-6">
                <h3 className="text-base/7 font-semibold text-gray-900">Close Ratios</h3>
              </div>
              <div className="border-t border-gray-100">
                <dl className="divide-y divide-gray-100">
                  <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">All-time</dt>
                    <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 font-semibold text-right">{pipelineMetrics.closeRatios.allTime}</dd>
                  </div>
                  <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Prior Quarter (Q2 '25)</dt>
                    <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 font-semibold text-right">{pipelineMetrics.closeRatios.priorQuarter}</dd>
                  </div>
                  <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Past 90 Days</dt>
                    <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 font-semibold text-right">{pipelineMetrics.closeRatios.past90}</dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Active Quotes (Savings >15%) Card */}
            <div className="overflow-hidden bg-white shadow-sm sm:rounded-lg border border-gray-200">
              <div className="px-4 py-3 sm:px-6">
                <h3 className="text-base/7 font-semibold text-gray-900">Active Quotes (Savings &gt;15%)</h3>
              </div>
              <div className="border-t border-gray-100">
                <dl className="divide-y divide-gray-100">
                  <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Count</dt>
                    <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 font-semibold text-right">{pipelineMetrics.activeQuotesSavings.count}</dd>
                  </div>
                  <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Potential Membership</dt>
                    <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 font-semibold text-right">{pipelineMetrics.activeQuotesSavings.membership}</dd>
                  </div>
                  <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Percent ALEs</dt>
                    <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 font-semibold text-right">{pipelineMetrics.activeQuotesSavings.percentALES}</dd>
                  </div>
                  <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Est. Revenue Opportunity</dt>
                    <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 font-semibold text-right">{pipelineMetrics.activeQuotesSavings.revenue}</dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Highest Value Opportunities Card */}
            <div className="overflow-hidden bg-white shadow-sm sm:rounded-lg border border-gray-200">
              <div className="px-4 py-3 sm:px-6">
                <h3 className="text-base/7 font-semibold text-gray-900">Highest Value Opportunities (Savings &gt;10%)</h3>
              </div>
              <div className="border-t border-gray-100">
                <div className="px-4 py-3">
                  <div className="space-y-1">
                    <div className="grid grid-cols-3 gap-4 text-xs text-gray-500 font-medium border-b border-gray-200 pb-1">
                      <span>Company</span>
                      <span className="text-center">Est Revenue</span>
                      <span className="text-center">#EEs</span>
                    </div>
                    {pipelineMetrics.highestOpps.map((opp, index) => (
                      <div key={index} className="grid grid-cols-3 gap-4 py-1 text-sm">
                        <span className="text-gray-700">{index + 1}. {opp.name}</span>
                        <span className="font-medium text-gray-900 text-center">{opp.estRev}</span>
                        <span className="font-medium text-gray-900 text-center">{opp.employees}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Other Sections Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="overflow-hidden bg-white shadow-sm sm:rounded-lg border border-gray-200">
              <div className="px-4 py-3 sm:px-6">
                <h3 className="text-base/7 font-semibold text-gray-900">Sold Cases</h3>
              </div>
              <div className="border-t border-gray-100">
                <div className="px-4 py-3">
                  <div className="space-y-1">
                    <div className="grid grid-cols-3 gap-4 text-xs text-gray-500 font-medium border-b border-gray-200 pb-1">
                      <span></span>
                      <span className="text-center">2025</span>
                      <span className="text-center">2026</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 py-2 text-sm">
                      <span className="text-gray-900 font-medium">Count of Employer Groups</span>
                      <span className="font-semibold text-center">{soldCases.y2025.groups}</span>
                      <span className="font-semibold text-center">{soldCases.y2026.groups}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 py-2 text-sm border-t border-gray-100">
                      <span className="text-gray-900 font-medium">Expected Enrollment</span>
                      <span className="font-semibold text-center">{soldCases.y2025.expectedEnrollment}</span>
                      <span className="font-semibold text-center">{soldCases.y2026.expectedEnrollment}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 py-2 text-sm border-t border-gray-100">
                      <span className="text-gray-900 font-medium">Est Annualized Revenue</span>
                      <span className="font-semibold text-center">{soldCases.y2025.annualRev}</span>
                      <span className="font-semibold text-center">{soldCases.y2026.annualRev}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 py-2 text-sm border-t border-gray-100">
                      <span className="text-gray-900 font-medium">Renewal Rate</span>
                      <span className="font-semibold text-center">{soldCases.y2025.renewal}</span>
                      <span className="font-semibold text-center">—</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-hidden bg-white shadow-sm sm:rounded-lg border border-gray-200">
              <div className="border-b border-gray-200 px-4 py-5 sm:px-6">
                <div className="-mt-2 -ml-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
                  <div className="mt-2 ml-4">
                    <h3 className="text-base font-semibold text-gray-900">Enrollments</h3>
                  </div>
                  <div className="mt-2 ml-4 shrink-0">
                    <Link
                      to="/enrollments"
                      className="relative inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      Pending ({enrollments.y2025.pending})
                    </Link>
                  </div>
                </div>
              </div>
              <div className="">
                <div className="px-4 py-3">
                  <div className="space-y-1">
                    <div className="grid grid-cols-3 gap-4 text-xs text-gray-500 font-medium border-b border-gray-200 pb-1">
                      <span></span>
                      <span className="text-center">2025</span>
                      <span className="text-center">2026*</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 py-2 text-sm">
                      <span className="text-gray-900 font-medium">Employees</span>
                      <span className="font-semibold text-center">{enrollments.y2025.employees}</span>
                      <span className="font-semibold text-center">{enrollments.y2026.employees}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 py-2 text-sm border-t border-gray-100">
                      <span className="text-gray-900 font-medium">Dependents</span>
                      <span className="font-semibold text-center">{enrollments.y2025.dependents}</span>
                      <span className="font-semibold text-center">{enrollments.y2026.dependents}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 py-2 text-sm border-t border-gray-100">
                      <span className="text-gray-900 font-medium">Total</span>
                      <span className="font-bold text-center">{enrollments.y2025.total}</span>
                      <span className="font-bold text-center">{enrollments.y2026.total}</span>
                    </div>
                  </div>
                </div>
                <div className="px-4 py-2 bg-gray-50">
                  <p className="text-xs text-gray-500">*2026 estimated by sold volume; OE begins Nov 3rd</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Simple Dashboard (Default View)
  return (
    <div className="min-h-screen bg-gradient-to-r from-teal-800 to-blue-900">
      {/* Main Banner */}
      <div className="bg-brand-gradient rounded-brand p-6 mb-8 text-white shadow-brand border border-[#46BC97]/40">
        <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
        <p className="text-sky-100">View key metrics and monitor performance</p>
      </div>

      {/* Feature Flag Demo - Only shown when enhancedDashboard is enabled */}
      {isFeatureEnabled('enhancedDashboard') && (
        <div className="mx-6 mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
          <div className="flex items-center space-x-2 text-blue-800 dark:text-blue-200">
            <CheckCircleIcon className="h-5 w-5" />
            <span className="font-medium">Enhanced Dashboard Features Enabled</span>
          </div>
          <p className="mt-1 text-sm text-blue-600 dark:text-blue-300">
            This section is only visible because the 'enhancedDashboard' feature flag is enabled in demo environment.
          </p>
        </div>
      )}

      <div className="p-6 space-y-6">
        {/* Stats Overview */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <Link to="/quotes">
            <motion.div
              key="totalQuotes"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className={`${theme.card} border border-slate-200 dark:border-[#46BC97]/40 p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 dark:bg-[#042C3A]/90 cursor-pointer hover:scale-105`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Total Quotes</p>
                  <h3 className="mt-2 text-3xl font-bold text-slate-800 dark:text-white">{dashboardData.totalQuotes}</h3>
                  <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">Click to view all quotes</p>
                </div>
                <div className={`p-3 rounded-xl bg-teal-50 dark:bg-teal-900/20 shadow-sm`}>
                  <DocumentTextIcon className="h-7 w-7 text-teal-600 dark:text-white" aria-hidden="true" />
                </div>
              </div>
            </motion.div>
          </Link>

          <Link to="/quotes">
            <motion.div
              key="activeQuotes"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className={`${theme.card} border border-slate-200 dark:border-[#46BC97]/40 p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 dark:bg-[#042C3A]/90 cursor-pointer hover:scale-105`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Active Quotes</p>
                  <h3 className="mt-2 text-3xl font-bold text-slate-800 dark:text-white">{dashboardData.activeQuotes}</h3>
                  <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">Click to view active quotes</p>
                </div>
                <div className={`p-3 rounded-xl bg-teal-50 dark:bg-teal-900/20 shadow-sm`}>
                  <DocumentTextIcon className="h-7 w-7 text-teal-600 dark:text-white" aria-hidden="true" />
                </div>
              </div>
            </motion.div>
          </Link>

          <Link to="/documents">
            <motion.div
              key="totalDocuments"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className={`${theme.card} border border-slate-200 dark:border-[#46BC97]/40 p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 dark:bg-[#042C3A]/90 cursor-pointer hover:scale-105`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Total Documents</p>
                  <h3 className="mt-2 text-3xl font-bold text-slate-800 dark:text-white">{dashboardData.totalDocuments}</h3>
                  <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">Click to view all documents</p>
                </div>
                <div className={`p-3 rounded-xl bg-teal-50 dark:bg-teal-900/20 shadow-sm`}>
                  <DocumentIcon className="h-7 w-7 text-teal-600 dark:text-white" aria-hidden="true" />
                </div>
              </div>
            </motion.div>
          </Link>

          <Link to="/documents">
            <motion.div
              key="recentUploads"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className={`${theme.card} border border-slate-200 dark:border-[#46BC97]/40 p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 dark:bg-[#042C3A]/90 cursor-pointer hover:scale-105`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Recent Uploads</p>
                  <h3 className="mt-2 text-3xl font-bold text-slate-800 dark:text-white">{dashboardData.recentUploads}</h3>
                  <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">Click to view recent uploads</p>
                </div>
                <div className={`p-3 rounded-xl bg-teal-50 dark:bg-teal-900/20 shadow-sm`}>
                  <ArrowUpTrayIcon className="h-7 w-7 text-teal-600 dark:text-white" aria-hidden="true" />
                </div>
              </div>
            </motion.div>
          </Link>
        </motion.div>

        {/* Additional Metrics (replacing Quick Actions) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className={`${theme.card} border border-slate-200 dark:border-[#46BC97]/40 p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 dark:bg-[#042C3A]/90`}
        >
          <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-4">Additional Metrics</h2>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Completion Rate */}
            <motion.div
              whileHover={{ scale: 1.01, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
              whileTap={{ scale: 0.98 }}
              className={`${theme.card} border border-slate-200 dark:border-[#46BC97]/40 p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 dark:bg-[#042C3A]/90`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Closed Ratio (last 90 days)</p>
                  <h3 className="mt-2 text-3xl font-bold text-slate-800 dark:text-white">
                    {dashboardData.completionRate || 0}%
                  </h3>
                  <p className="text-sm font-medium text-teal-600 dark:text-white">
                    <span className="flex items-center">
                      <ArrowUpIcon className="h-3 w-3 mr-1" /> 
                      Quotes completed/sold in the last 90 days
                    </span>
                  </p>
                </div>
                <div className={`p-3 rounded-xl bg-green-50 dark:bg-green-900/20 shadow-sm`}>
                  <CheckCircleIcon className="h-7 w-7 text-green-600 dark:text-white" aria-hidden="true" />
                </div>
              </div>
            </motion.div>

            {/* Average Quote Value */}
            <motion.div
              whileHover={{ scale: 1.01, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
              whileTap={{ scale: 0.98 }}
              className={`${theme.card} border border-slate-200 dark:border-[#46BC97]/40 p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 dark:bg-[#042C3A]/90`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Average Quote Value</p>
                  <h3 className="mt-2 text-3xl font-bold text-slate-800 dark:text-white">
                    ${dashboardData.avgQuoteValue || 0}
                  </h3>
                  <p className="text-sm font-medium text-teal-600 dark:text-white">
                    <span className="flex items-center">
                      <span>Per Employee Per Month</span>
                    </span>
                  </p>
                </div>
                <div className={`p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 shadow-sm`}>
                  <ChartBarIcon className="h-7 w-7 text-blue-600 dark:text-white" aria-hidden="true" />
                </div>
              </div>
            </motion.div>

            {/* Status Distribution */}
            <motion.div
              whileHover={{ scale: 1.01, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
              whileTap={{ scale: 0.98 }}
              className={`${theme.card} border border-slate-200 dark:border-[#46BC97]/40 p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 dark:bg-[#042C3A]/90`}
            >
              <div className="flex flex-col">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Quotes by Status</p>
                  <div className={`p-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 shadow-sm`}>
                    <ClockIcon className="h-6 w-6 text-amber-600 dark:text-white" aria-hidden="true" />
                  </div>
                </div>
                
                <div className="space-y-2 mt-2">
                  {dashboardData.statusCounts ? (
                    Object.entries(dashboardData.statusCounts).map(([status, count]) => {
                      // Map status to display name
                      const getStatusDisplayName = (status: string) => {
                        switch (status.toLowerCase()) {
                          case 'active':
                          case 'approved':
                            return 'Active';
                          case 'pending':
                            return 'Pending';
                          case 'inactive':
                            return 'Inactive';
                          case 'rejected':
                            return 'Rejected';
                          case 'sold':
                            return 'Sold';
                          default:
                            return status.charAt(0).toUpperCase() + status.slice(1);
                        }
                      };
                      
                      return (
                        <div key={status} className="flex justify-between items-center p-2 hover:bg-slate-50 dark:hover:bg-[#46BC97]/10 rounded-lg transition-colors duration-200">
                          <span className="font-medium text-slate-700 dark:text-white">{getStatusDisplayName(status)}</span>
                          <span className="font-bold text-slate-800 dark:text-white">{count}</span>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center text-slate-500 dark:text-slate-400 p-2 rounded-lg bg-slate-50 dark:bg-[#031C25] dark:border dark:border-[#46BC97]/20">No data available</div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* This Week's Quotes */}
            <motion.div
              whileHover={{ scale: 1.01, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
              whileTap={{ scale: 0.98 }}
              className={`${theme.card} border border-slate-200 dark:border-[#46BC97]/40 p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 dark:bg-[#042C3A]/90`}
            >
              <div className="flex flex-col">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">This Week's Quotes</p>
                  <div className={`p-2 rounded-xl bg-purple-50 dark:bg-purple-900/20 shadow-sm`}>
                    <DocumentPlusIcon className="h-6 w-6 text-purple-600 dark:text-white" aria-hidden="true" />
                  </div>
                </div>
                
                                  <div className="text-center p-4">
                    <h3 className="text-3xl font-bold text-slate-800 dark:text-white">{dashboardData.thisWeeksQuotes}</h3>
                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-1">
                      {dashboardData.thisWeeksQuotes === 1 ? 'quote' : 'quotes'} in the last 7 days
                    </p>
                  </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className={`${theme.card} border border-slate-200 dark:border-[#46BC97]/40 p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 dark:bg-[#042C3A]/90`}
        >
          <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-4">Recent Activity</h2>
          <div className="mt-4 space-y-4">
            {dashboardData.recentQuotes && dashboardData.recentQuotes.length > 0 ? (
              <ul className="divide-y divide-slate-200 dark:divide-[#031C25]">
                {dashboardData.recentQuotes.map((quote: any, index: number) => (
                  <motion.li
                    key={quote.id || quote.submissionId}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + index * 0.1 }}
                    className="py-4"
                  >
                    <Link
                      to={`/quotes/${quote.submissionId}?brokerId=${quote.brokerId}&employerId=${quote.employerId}`}
                      className="flex items-center space-x-4 hover:bg-slate-50 dark:hover:bg-[#46BC97]/10 rounded-lg p-2 -m-2 transition-colors duration-200 group"
                    >
                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        className={`p-2 rounded-lg ${
                          (quote.status?.toLowerCase() === 'active' || quote.status?.toLowerCase() === 'approved') ? 'bg-teal-50 dark:bg-teal-900/20' : 
                          quote.status?.toLowerCase() === 'pending' ? 'bg-amber-50 dark:bg-amber-900/20' : 
                          quote.status?.toLowerCase() === 'inactive' ? 'bg-gray-50 dark:bg-gray-800' : 'bg-red-50 dark:bg-red-900/20'
                        } shadow-sm group-hover:shadow-md transition-shadow duration-200`}
                      >
                        <span className="font-medium dark:text-white text-seafoam group-hover:text-seafoam-600 dark:group-hover:text-sky-400 transition-colors duration-200">
                          {quote.name || quote.companyName}
                        </span>
                      </motion.div>
                      <motion.div className="flex-1">
                        <p className="font-medium text-slate-700 dark:text-slate-200">
                          {
                            (quote.status?.toLowerCase() === 'active' || quote.status?.toLowerCase() === 'approved') ? 'Approved' : 
                            quote.status?.toLowerCase() === 'pending' ? 'Pending' : 
                            quote.status?.toLowerCase() === 'inactive' ? 'Inactive' : 'Status Unknown'
                          }
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {quote.date || quote.createdAt || new Date().toLocaleDateString()}
                        </p>
                      </motion.div>
                      <motion.div className="flex-shrink-0 flex">
                        <StatusBadge status={quote.status || ''} theme={theme} />
                      </motion.div>
                    </Link>
                  </motion.li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-5 text-center text-sm text-slate-500 dark:text-slate-400 rounded-lg bg-slate-50 dark:bg-[#031C25] dark:border dark:border-[#46BC97]/20">
                No recent quotes found.
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard; 